// redesign/HoldingRow.tsx — one holding in the sidebar list (new design .row/.coin).
// Manual holdings get edit/delete; integration (synced) holdings get a lock, plus a
// targeted refresh button when the source supports it (MetaMask).
import React, { useState } from 'react';
import { maskUSD, amountDisplay } from './format';
import AmountText from './AmountText';
import { shortenAddresses } from './visual';
import type { Valued } from './portfolio';

interface HoldingRowProps {
  h: Valued;
  hidden: boolean;
  active: boolean;
  onSelect: (id: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onRefresh?: () => Promise<unknown>;
}

export default function HoldingRow({ h, hidden, active, onSelect, onEdit, onDelete, onRefresh }: HoldingRowProps) {
  const a = h.asset;
  const amt = amountDisplay(a.amount, h.scale, h.unitUsd);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    onRefresh().catch(() => {}).finally(() => setRefreshing(false));
  };
  return (
    <div
      className={'row' + (active ? ' is-active' : '')}
      onClick={() => onSelect(h.id)}
      title={a.normalizedName}
    >
      <div className="coin" style={{ background: h.color + '22', color: h.color, border: '1px solid ' + h.color + '55' }}>
        {h.sym}
        <span className="coin__src" style={{ background: h.source.tint }}>{h.source.mark}</span>
      </div>
      <div className="row__main">
        <div className="row__top">
          <span className="row__asset">{h.code}</span>
          <span className="tag">{h.kind}</span>
        </div>
        <div className="row__sub">{shortenAddresses(a.normalizedName)}</div>
      </div>
      <div className="row__end">
      <div className="row__right">
        <div className="row__usd num">{maskUSD(h.usd, hidden)}</div>
        <div className="row__amt num">
          <AmountText amt={amt} code={h.code} hidden={hidden} />
        </div>
      </div>
      <div className="row__actions">
        {h.manual ? (
          <>
            <button className="act" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
            </button>
            <button className="act del" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
            </button>
          </>
        ) : (
          <>
            {onRefresh ? (
              <button className={'act refresh' + (refreshing ? ' spinning' : '')} title="Refresh balance"
                      disabled={refreshing} onClick={refresh}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
                </svg>
              </button>
            ) : (
              /* spacer in the edit slot so the lock lines up with the delete button */
              <span className="act" aria-hidden="true" style={{ visibility: 'hidden' }} />
            )}
            <span className="act lock" title={'Synced from ' + h.source.label + " — managed automatically"}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
            </span>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
